import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import WordsRise from "./WordsRise";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";

const EASE = [0.16, 1, 0.3, 1];

export default function Founder() {
  const sectionRef = useRef(null);
  const reduced = usePrefersReducedMotion();

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [30, -30]);

  return (
    <section
      ref={sectionRef}
      id="founder"
      data-testid="founder-section"
      className="relative scroll-mt-24 border-t border-white/10 bg-noir py-24 md:py-32 lg:py-40"
    >
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-16">
        {/* LEFT — portrait (5 cols) */}
        <div className="lg:col-span-5">
          <motion.figure
            className="relative mx-auto w-full max-w-[340px] lg:mx-0"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: EASE }}
          >
            <div className="relative overflow-hidden rounded-[4px]" style={{ aspectRatio: "4 / 5" }}>
              <motion.img
                data-testid="founder-portrait"
                src="/rab.jpg"
                alt="Rab Govil, Founder of Govil Ventures"
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover object-top"
                style={{ y: reduced ? 0 : parallaxY, scale: 1.06 }}
                initial={{ opacity: 0.6, filter: "grayscale(100%)" }}
                whileInView={{ opacity: 1, filter: "grayscale(0%)" }}
                viewport={{ once: true, margin: "-12%" }}
                transition={{ duration: 0.9, ease: EASE }}
              />
              {/* subtle warm grade */}
              <div className="pointer-events-none absolute inset-0 [background:linear-gradient(160deg,rgba(201,162,39,0.10)_0%,transparent_45%,rgba(10,10,10,0.28)_100%)]" />
              {/* gold hairline tracing bottom + left, drawing in */}
              <motion.span
                aria-hidden
                className="absolute bottom-0 left-0 h-px w-full origin-left bg-gold"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.25 }}
              />
              <motion.span
                aria-hidden
                className="absolute bottom-0 left-0 h-full w-px origin-bottom bg-gold"
                initial={{ scaleY: 0 }}
                whileInView={{ scaleY: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.35 }}
              />
            </div>
            <figcaption className="mt-4 text-[0.68rem] uppercase tracking-[0.28em] text-gold">
              Rab Govil &mdash; Founder
            </figcaption>
          </motion.figure>
        </div>

        {/* RIGHT — content (7 cols) */}
        <div className="lg:col-span-7 lg:pl-6">
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE }}
            className="mb-7 text-[0.72rem] font-semibold uppercase tracking-[0.32em] text-gold"
          >
            The Person Behind the Firm
          </motion.p>

          <WordsRise
            testid="founder-pullquote"
            text="My purpose is to help my teams win."
            className="max-w-3xl font-serif leading-[1.02] tracking-tight text-cream"
            style={{ fontSize: "clamp(2.25rem, 4.6vw, 4rem)", fontWeight: 700 }}
          />

          <motion.p
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
            className="mt-8 max-w-2xl leading-relaxed text-mute"
          >
            Rab Govil has spent twenty-five years building companies, and the same
            amount of time discovering that the work he cares about most isn&rsquo;t
            the building of the business &mdash; it&rsquo;s the people doing it.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
            className="mt-12"
          >
            <Link
              to="/founder"
              data-testid="founder-read-more"
              className="group inline-flex items-center gap-2 text-[0.72rem] font-semibold uppercase tracking-[0.2em] text-cream"
            >
              <span className="relative">
                The why?
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
              </span>
              <ArrowRight className="h-4 w-4 text-gold transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
